import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { lazy, Suspense } from 'react';
import './index.css';

// Customer pages — eagerly loaded (part of main bundle)
import HomePage from './pages/HomePage';
import CategoryPage from './pages/CategoryPage';
import ProductPage from './pages/ProductPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderTrackingPage from './pages/OrderTrackingPage';
import WishlistPage from './pages/WishlistPage';
import SearchPage from './pages/SearchPage';
import LoginPage from './pages/LoginPage';
import AccountPage from './pages/AccountPage';
import OrdersPage from './pages/OrdersPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ReturnsPage from './pages/ReturnsPage';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';

// Admin pages — lazy loaded so they are NOT bundled into the customer build
const AdminLayout   = lazy(() => import('./admin/AdminLayout'));
const AdminDashboard  = lazy(() => import('./admin/pages/AdminDashboard'));
const AdminProducts   = lazy(() => import('./admin/pages/AdminProducts'));
const AdminOrders     = lazy(() => import('./admin/pages/AdminOrders'));
const AdminCustomers  = lazy(() => import('./admin/pages/AdminCustomers'));
const AdminCoupons    = lazy(() => import('./admin/pages/AdminCoupons'));
const AdminInventory  = lazy(() => import('./admin/pages/AdminInventory'));
const AdminCategories = lazy(() => import('./admin/pages/AdminCategories'));
const AdminBanners    = lazy(() => import('./admin/pages/AdminBanners'));
const AdminReports    = lazy(() => import('./admin/pages/AdminReports'));
const AdminLogin      = lazy(() => import('./admin/AdminLogin'));

const qc = new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 30000 } } });

const AdminFallback = <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#888', fontSize: 14 }}>Loading…</div>;

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <Toaster position="top-center" toastOptions={{ style: { fontFamily: 'var(--body)', fontSize: 13, background: '#2A1F1A', color: '#FAF6EE' } }} />
        <ErrorBoundary>
          <Routes>
            {/* Customer */}
            <Route element={<Layout />}>
              <Route path="/" element={<ErrorBoundary><HomePage /></ErrorBoundary>} />
              <Route path="/search" element={<ErrorBoundary><SearchPage /></ErrorBoundary>} />
              <Route path="/category/:slug" element={<ErrorBoundary><CategoryPage /></ErrorBoundary>} />
              <Route path="/product/:slug" element={<ErrorBoundary><ProductPage /></ErrorBoundary>} />
              <Route path="/cart" element={<ErrorBoundary><CartPage /></ErrorBoundary>} />
              <Route path="/wishlist" element={<ErrorBoundary><WishlistPage /></ErrorBoundary>} />
              <Route path="/login" element={<ErrorBoundary><LoginPage /></ErrorBoundary>} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/returns" element={<ReturnsPage />} />
              <Route element={<ProtectedRoute />}>
                <Route path="/checkout" element={<ErrorBoundary><CheckoutPage /></ErrorBoundary>} />
                <Route path="/orders" element={<ErrorBoundary><OrdersPage /></ErrorBoundary>} />
                <Route path="/orders/:id/track" element={<ErrorBoundary><OrderTrackingPage /></ErrorBoundary>} />
                <Route path="/account" element={<ErrorBoundary><AccountPage /></ErrorBoundary>} />
              </Route>
              <Route path="*" element={
                <div style={{ textAlign: 'center', padding: '80px 24px' }}>
                  <div className="serif" style={{ fontSize: 48, marginBottom: 12 }}>404</div>
                  <div style={{ fontSize: 16, color: 'var(--muted)', marginBottom: 24 }}>This page could not be found.</div>
                  <a href="/" className="btn btn-primary" style={{ display: 'inline-block' }}>Go Home</a>
                </div>
              } />
            </Route>

            {/* Admin — lazy loaded, separate chunk */}
            <Route path="/admin/login" element={<Suspense fallback={AdminFallback}><AdminLogin /></Suspense>} />
            <Route path="/admin" element={
              <AdminRoute>
                <Suspense fallback={AdminFallback}>
                  <AdminLayout />
                </Suspense>
              </AdminRoute>
            }>
              <Route index element={<Suspense fallback={null}><AdminDashboard /></Suspense>} />
              <Route path="products"   element={<Suspense fallback={null}><AdminProducts /></Suspense>} />
              <Route path="orders"     element={<Suspense fallback={null}><AdminOrders /></Suspense>} />
              <Route path="customers"  element={<Suspense fallback={null}><AdminCustomers /></Suspense>} />
              <Route path="coupons"    element={<Suspense fallback={null}><AdminCoupons /></Suspense>} />
              <Route path="inventory"  element={<Suspense fallback={null}><AdminInventory /></Suspense>} />
              <Route path="categories" element={<Suspense fallback={null}><AdminCategories /></Suspense>} />
              <Route path="banners"    element={<Suspense fallback={null}><AdminBanners /></Suspense>} />
              <Route path="reports"    element={<Suspense fallback={null}><AdminReports /></Suspense>} />
            </Route>
          </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
